import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import useEmployeeFinanceText from './employeeFinanceText';
import { DEFAULT_FILTERS, YES_NO_KEYS, isYes } from './employeeFinanceColumns';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null ||
    text === undefined ||
    text === '' ||
    Number.isNaN(text) ||
    (typeof text === 'string' &&
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength
    ? `${textStr.substring(0, maxLength)}...`
    : textStr;
};

/**
 * Data of Çalışan Finans Yönetimi: the employees (opens on the active ones,
 * as before), the search (name, surname, TC no), the column filters and the
 * Excel export (its old layout and English headers kept).
 */
const useEmployeeFinance = () => {
  const { label } = useEmployeeFinanceText();
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = querySnapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  useEffect(() => {
    fetchEmployees();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    // Search: name, surname and TC number
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          (emp.eName && emp.eName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.eSurname && emp.eSurname.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.tcNumber && emp.tcNumber.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Column filters
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        if (key === 'Active') {
          filtered = filtered.filter((item) => String(item[key]) === String(filters[key]));
        } else if (YES_NO_KEYS.includes(key)) {
          // Evet / Hayır: hem 'Yes'/'No' hem true/false kayıtları
          filtered = filtered.filter((item) => isYes(item[key]) === (filters[key] === 'Yes'));
        } else {
          filtered = filtered.filter((item) =>
            String(item[key] ?? '')
              .toLowerCase()
              .includes(String(filters[key]).toLowerCase())
          );
        }
      }
    });

    // Sorted by first name (Ad column)
    return [...filtered].sort((a, b) => {
      const nameA = (a.eName || '').toString().toLocaleLowerCase('tr-TR');
      const nameB = (b.eName || '').toString().toLocaleLowerCase('tr-TR');
      return nameA.localeCompare(nameB, 'tr');
    });
  }, [employees, filters, searchTerm]);

  const stats = useMemo(() => ({
    total: employees.length,
    active: employees.filter((emp) => emp.Active === 1).length,
    inactive: employees.filter((emp) => emp.Active !== 1).length,
  }), [employees]);

  const selectedEmployee = useMemo(() => employees.find((emp) => emp.id === selectedId) || null, [employees, selectedId]);

  const handleFilter = useCallback((column, value) => setFilters((current) => ({ ...current, [column]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Employees');
      worksheet.columns = [
        { header: 'First Name', key: 'eName', width: 15 },
        { header: 'Last Name', key: 'eSurname', width: 15 },
        { header: 'TC No', key: 'tcNumber', width: 15 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Department', key: 'departman', width: 20 },
        { header: 'Status', key: 'Active', width: 10 },
      ];
      employees.forEach((emp) => {
        worksheet.addRow({
          eName: truncateText(emp.eName),
          eSurname: truncateText(emp.eSurname),
          tcNumber: truncateText(emp.tcNumber),
          phone: truncateText(emp.phone),
          departman: truncateText(emp.departman),
          Active: emp.Active === 1 ? 'Active' : 'Inactive',
        });
      });
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'employees.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(label('toast.excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(label('toast.excelError'));
    }
  };

  return {
    employees,
    isLoading,
    filteredEmployees,
    stats,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    selectedEmployee,
    setSelectedId,
    downloadExcel,
  };
};

export default useEmployeeFinance;
